import { Dialog, DialogContent } from '@mui/material';
import { useTransactionFormModal } from '@/hooks/stores/useTransactionFormModal';
import { useGetBankAccountsList } from '@/hooks/requests/useBankAccounts';
import { useGetExternalAccountsList } from '@/hooks/requests/useExternalAccounts';
import { ExternalBankAccountStatus } from '@/types/externalAccounts';
import { useGetWireRecipientsList } from '@/hooks/requests/useWireRecipients';
import { titleCase } from '@/utils/stringCaser';
import MissingAccountsModal from './MissingAccountsModal';
import { ROUTES } from '@/constants/routes';
import { BankAccountListSortBy } from '@/types/bankAccounts';
import { SVLoader } from '@/components/core';
import TransactionForm from '@/components/transactionForm/TransactionForm';

export default function TransactionFormModal() {
  const { open, bankAccountId, customerId, type, closeTransactionFormModal } =
    useTransactionFormModal();

  const isDeposit = type === 'deposit';
  const isWire = type === 'wire';

  const { accountsForSelection, accountsIsPending } = useGetBankAccountsList(
    {
      consumer_id: customerId,
      records_per_page: 100,
      page: 0,
      sort_by: BankAccountListSortBy.AVAILABLE_BALANCE_DESCENDING,
    },
    open,
  );
  const { externalAccountsForSelection, externalAccountsIsPending } =
    useGetExternalAccountsList(
      {
        consumer_ids: [customerId],
        statuses: [ExternalBankAccountStatus.VERIFIED],
        records_per_page: 100,
        page: 0,
      },
      open,
    );
  const { wireRecipientsForSelection, isWireRecipientsPending } =
    useGetWireRecipientsList(
      {
        consumer_id: customerId,
        status: 'active',
        records_per_page: 100,
        page: 0,
      },
      open,
    );

  const isLoading = isWire
    ? accountsIsPending || externalAccountsIsPending || isWireRecipientsPending
    : accountsIsPending || externalAccountsIsPending;

  if (isLoading) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => closeTransactionFormModal()}>
        <DialogContent>
          <SVLoader />
        </DialogContent>
      </Dialog>
    );
  }

  const mergedInternalAndExternalAccounts = [
    ...accountsForSelection,
    ...externalAccountsForSelection,
  ];
  if (mergedInternalAndExternalAccounts.length <= 1 && !isDeposit && !isWire) {
    return (
      <MissingAccountsModal
        title="Cannot Request a Transfer"
        content="The selected owner must have at least two bank accounts setup in order to transfer money"
        submitText="Add Bank Account"
        submitRoute={ROUTES.BANK_ACCOUNT_APPLICATIONS}
      />
    );
  }

  const fromAccounts = isDeposit
    ? externalAccountsForSelection
    : isWire
      ? accountsForSelection
      : mergedInternalAndExternalAccounts;
  if (fromAccounts.length === 0 && isDeposit) {
    return (
      <MissingAccountsModal
        title="Cannot Request a Deposit"
        content="The selected owner must have at least one External Account approved. Please add an External Account or ask the owner to add their own via the app."
        submitText="Add External Account"
        submitRoute={`${ROUTES.EXTERNAL_ACCOUNTS}/add`}
      />
    );
  }

  const toAccounts = isDeposit
    ? accountsForSelection
    : isWire
      ? wireRecipientsForSelection
      : mergedInternalAndExternalAccounts;
  if (toAccounts.length === 0 && isWire) {
    return (
      <MissingAccountsModal
        title="Cannot Request a Wire"
        content="The selected owner must have at least one Wire Recipient approved. Please add a Wire Recipient or ask the owner to approve any requests they may have."
        submitText="Add Wire Recipient"
        submitRoute={`${ROUTES.CUSTOMERS}/${customerId}${ROUTES.WIRE_RECIPIENTS}/add`}
      />
    );
  }

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => closeTransactionFormModal()}>
      <DialogContent>
        <TransactionForm
          customerId={customerId}
          confirmHeader={`Confirm ${titleCase(type)}`}
          formHeader={`Request a ${titleCase(type)}`}
          toAccounts={toAccounts}
          fromAccounts={fromAccounts}
          onSubmit={closeTransactionFormModal}
          onCancel={closeTransactionFormModal}
          isWire={isWire}
          preselectedFromAccount={isWire ? bankAccountId : ''}
          preselectedToAccount={isDeposit ? bankAccountId : ''}
        />
      </DialogContent>
    </Dialog>
  );
}
